export type TutorialId = 'invite' | 'history' | 'measurements' | 'visualization' | 'adherence' | 'export' | 'daily' | 'timezone' | 'chat';

export interface ResearcherTutorial {
  id: TutorialId;
  title: string;
  summary: string;
  where: string;
  steps: string[];
  notes: { title: string; text: string }[];
  related?: TutorialId[];
}

export const researcherTutorials: ResearcherTutorial[] = [
  {
    id: 'invite',
    title: 'Invite a participant',
    summary: 'Send an invitation and check whether someone has joined.',
    where: 'Open your study → Invite Participants',
    steps: [
      'Enter the participant’s email. You can add more people to the same invitation list.',
      'Review the subject and message, then send the invitation. StudySync adds the enrollment link to the email.',
      'For Fitbit, ask the participant to open their own invitation link, connect their Google account and approve access to their Fitbit data. They do not need a researcher account.',
      'Check Pending participants while they finish enrollment. Once they join, look for them in Participants.',
    ],
    notes: [
      { title: 'Use the right invitation', text: 'A personal Fitbit link belongs to the person and study it was sent to. If it expires, send a new invitation from that study.' },
      { title: 'Using HealthKit?', text: 'Use the study’s iOS invitation or QR code. The participant connects through the StudySync app on their iPhone.' },
    ],
  },
  {
    id: 'history',
    title: 'Understand participant data',
    summary: 'Read the history dates, available dates and green progress bar.',
    where: 'Open your study → Participants',
    steps: [
      'After Fitbit enrollment, StudySync collects the participant’s past data in the background. You can leave the page while it runs.',
      'Participant history shows the date range Fitbit allows StudySync to collect.',
      'Data currently available shows the earliest and latest records already saved. You can export saved data while the rest is still being collected.',
      'Use the green bar to check historical collection progress. Open the ? beside it for details, or use Refresh to check again.',
    ],
    notes: [
      { title: 'What does 100% mean?', text: 'StudySync has finished checking the historical measurement windows. It does not mean every day or every measurement has data. Device use and Fitbit availability can leave gaps.' },
      { title: 'What happens after that?', text: 'Background collection checks the previous seven days for new or updated Fitbit readings. It is scheduled at midnight UTC; queued work may finish later. This runs separately from the daily export schedules you create.' },
      { title: 'An outdated study', text: 'Studies marked Outdated still let you review existing information, but cannot collect or export through the new Fitbit system. Create a new study for new Fitbit enrollment.' },
    ],
  },
  {
    id: 'export',
    title: 'Extract participant data',
    summary: 'Export participant data to Google Cloud. Choose the people, measurements, dates, timezone and file format. The walkthrough below shows the Fitbit form.',
    where: 'Open your study → Data extraction',
    steps: [
      'Participants: choose Fitbit, then add one or more people. Check the available dates shown beside each name. One job can include several participants, with a separate file for each person.',
      'Measurements: tick the metrics you need, select a whole category, or select all. Verified metric filters the list; it does not select the measurements for you. You can also include unverified metrics.',
      'Dates: use full history, count forward from the earliest date or backward from the latest date, or enter a custom range. Everyone uses the same dates unless you tick Separate participants and set dates for each person.',
      'Timezone for participant: choose Participant timezone for each person’s own calendar day, using the local time Fitbit records with each reading. Choose UTC timezone to use the same UTC dates for everyone.',
      'Name and file format: give the extraction a name, then choose Research tables (.zip), JSON, JSON Lines or CSV. With Research tables, open Customize & preview to adjust the columns and preview a participant’s stored data.',
      'Google Cloud: click Start extracting to Google Cloud. Watch Your extraction jobs until it is Ready, then open View details. Choose a participant to see their file, tracking ID and file size. Open its Google Cloud link or use Download.',
    ],
    notes: [
      { title: 'Where are the files?', text: 'Each study has its own Google Cloud bucket. Fitbit exports are grouped by researcher name, then participant tracking ID. Use the file’s Google Cloud link to open its location, or download it from the extraction job.' },
      { title: 'Which format should I choose?', text: 'Choose Research tables for separate CSV tables with column definitions. JSON keeps the original structure. JSON Lines puts each record on its own line. A single CSV keeps nested values in JSON columns.' },
      { title: 'Which timezone should I choose?', text: 'Use Participant timezone when you want each person’s local day, such as their Monday activity. Use UTC when you need everyone’s dates to have the same time boundaries. This changes which readings fall within your selected dates; the original recorded timestamps are preserved.' },
      { title: 'Want this every day?', text: 'Use Set up daily extraction to save yesterday’s data automatically. You choose the run time, and email delivery is optional. See the daily export guide below.' },
    ],
    related: ['daily', 'timezone', 'measurements'],
  },
  {
    id: 'measurements',
    title: 'Choose measurements',
    summary: 'Find metrics, select a category and understand the labels.',
    where: 'Data extraction or Visualization → Measurements',
    steps: [
      'Search for a measurement, or choose a category such as Activity & fitness, Heart & breathing or Sleep.',
      'Tick the measurements you want. Select all measurements chooses the full list; the category selection button chooses the current group. Click the same button again to deselect that group.',
      'Click Verified metric to show measurements StudySync has verified. Click it again to show the full list. Your chosen measurements stay selected.',
      'Click the ? on an Unverified metric label to read what it means. You can still choose that measurement.',
    ],
    notes: [
      { title: 'Verified does not mean available for everyone', text: 'A participant may have no readings for a metric, even if it is verified. Check their dates and try a metric they normally record.' },
      { title: 'Similar names can describe different data', text: 'Daily summaries, recorded intervals and workout sessions cover different things. For example, active minutes measure time, while Active Zone Minutes are weighted credits. Avoid adding overlapping summaries together.' },
    ],
  },
  {
    id: 'visualization',
    title: 'Visualize data',
    summary: 'Choose what to plot and make sense of an empty chart.',
      where: 'Open your study → Visualization',
      steps: [
        'Use Visualization in the top menu for charts, or open Adherence for Data coverage and Study adherence. Your selections and results stay in place as you move between them.',
      'Choose the data source, participant and measurements you want to view.',
      'Choose dates within the participant’s available data range, then click Visualize.',
      'Check Displayed charts for the dates shown. After changing your selection, click Visualize again to update the charts.',
      'Use the chart’s field selector or ? button when a measurement has several values or needs more explanation.',
    ],
    notes: [
      { title: 'No measurements available?', text: 'Check that this metric has data in the selected dates and that historical collection has reached those dates. A participant’s overall available range includes all metrics; an individual metric may cover less.' },
      { title: 'Preview versus export', text: 'A preview can show only part of a large dataset. When the page says it is limited, narrow the date range for a fuller chart or export the selected dates.' },
      { title: 'Some values are settings', text: 'Heart-rate zone limits describe BPM boundaries. They are not recorded heart rates or minutes spent in a zone.' },
    ],
  },
  {
    id: 'adherence',
    title: 'Check coverage and adherence',
    summary: 'See what was recorded and compare it with your study targets.',
      where: 'Open your study → Adherence → Data coverage or Study adherence',
      steps: [
        'Use the Adherence menu to switch between Data coverage and Study adherence. Open Visualization for charts; all three stay inside your study.',
      'Use Data coverage to inspect recording dates, gaps and measurement availability.',
      'In Study adherence, choose participants and dates, then run the analysis.',
      'Review Study targets. Use Edit thresholds to set the supported targets for your study.',
      'Compare participants and daily results. Open Alerts to review low-adherence days and follow up when needed.',
    ],
    notes: [
      { title: 'How the Fitbit score works', text: 'Each supported target contributes a percentage, capped at 100%. The daily score is their average. For example, meeting one target fully and another halfway gives 75%.' },
      { title: 'Unknown is different from zero', text: 'Uncollected days and the current day are left unknown. A completed, collected day with no reading for a supported target counts as zero for that target. Unsupported targets need review before a score is available.' },
      { title: 'An alert is a reason to check', text: 'A Fitbit score below 70% creates a review item. It does not prove someone failed to wear their device. These reports currently use UTC days; the export timezone choice applies to exports.' },
    ],
  },
  {
    id: 'daily',
    title: 'Set up daily exports',
    summary: 'Save yesterday’s data automatically, with optional email delivery.',
    where: 'Data extraction → Set up daily extraction',
    steps: [
      'Select the participants. All measurements are selected initially; change the selection for each person if needed.',
      'Choose the extraction name, file format and timezone for the participant’s data.',
      'Set the daily run time and its time zone. Files will be saved to the study’s Google Cloud bucket.',
      'If you want email delivery, turn on Email a ZIP attachment and check the recipients. Leave it off to save only to Google Cloud.',
      'Save the schedule. Use View daily extractions whenever you want to change people, measurements, time or email recipients, or pause the schedule.',
    ],
    notes: [
      { title: 'Collection and export are separate', text: 'Background collection brings Fitbit readings into StudySync. A daily export saves yesterday’s stored data as files. Creating an export schedule does not change when Fitbit collection runs.' },
      { title: 'Email delivery', text: 'The ZIP is sent after the files finish. If it is too large to attach, the email includes Google Cloud links instead.' },
    ],
  },
  {
    id: 'timezone',
    title: 'Choose an export timezone',
    summary: 'Decide what a selected date means for your participants.',
    where: 'Data extraction or daily extraction → Timezone for participant',
    steps: [
      'Choose Participant timezone to use the local time information Fitbit saves with each reading. This is useful when you want each person’s own calendar day.',
      'Choose UTC timezone to use the same UTC date boundaries for everyone. This is useful when comparing recordings made at the same time in different locations.',
      'For a daily export, also set the time zone beside the run time. That controls when the schedule starts, separately from which data dates it exports.',
    ],
    notes: [
      { title: 'A simple example', text: 'A reading taken at 9 PM in California can fall on the following date in UTC. The timezone choice decides which selected day includes that reading; the original timestamp is preserved.' },
    ],
  },
  {
    id: 'chat',
    title: 'Use study chat',
    summary: 'Set up secure messaging and keep a way to recover access.',
    where: 'Open your study → Chat',
    steps: [
      'Open Chat and follow the setup steps to add a passkey and save your recovery key somewhere safe.',
      'Choose a participant’s conversation. The participant needs a StudySync app version that supports chat.',
      'Unlock chat when prompted. You can read and send messages once your encryption keys and conversation access are ready.',
      'Open the encryption settings to manage passkeys, recovery and trusted-browser access.',
    ],
    notes: [
      { title: 'Keep your recovery key', text: 'It helps you regain access when you cannot use your passkey. StudySync cannot read your encrypted messages for you.' },
      { title: 'After a key change', text: 'A colleague may need to confirm your new encryption key. An emergency conversation reset starts a new readable history; it does not recover old messages.' },
    ],
  },
];

export function findTutorials(query: string) {
  const words = query.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
  return researcherTutorials.filter(guide => {
    const text = [guide.title, guide.summary, guide.where, ...guide.steps, ...guide.notes.flatMap(note => [note.title, note.text])].join(' ').toLocaleLowerCase();
    return words.every(word => text.includes(word));
  });
}
